import { useEffect, useRef, useState } from "react"
import type { ReactNode } from "react"
import { Icon, type IconName } from "./icons.tsx"

/**
 * 단추를 누르면 아래로 열리는 작은 판.
 *
 * `<details>` 로 만든다 — 열고 닫는 상태를 브라우저가 갖고, 키보드(Enter/Space)도 그냥 된다.
 * 바깥을 누르면 닫는 것만 우리가 붙인다.
 */
export function Menu({
  label,
  icon,
  align = "end",
  iconOnly = false,
  children,
}: {
  label: string
  icon?: IconName
  /** 판이 단추의 어느 쪽에 붙을지. 화면 오른쪽 끝의 단추는 "end". */
  align?: "start" | "end"
  /** 아이콘만 보인다(머리에 놓는 벨처럼). 이름은 스크린리더에만 남긴다. */
  iconOnly?: boolean
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDetailsElement>(null)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false)
    }
    document.addEventListener("pointerdown", onPointerDown)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("pointerdown", onPointerDown)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [open])

  return (
    <details
      className="menu"
      ref={ref}
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary
        className={iconOnly ? "menu__button menu__button--icon" : "menu__button btn btn--md"}
        title={iconOnly ? label : undefined}
      >
        {icon ? <Icon name={icon} size={iconOnly ? 18 : 15} /> : null}
        <span className={iconOnly ? "sr-only" : undefined}>{label}</span>
        {iconOnly ? null : <Icon name="chevronDown" size={14} />}
      </summary>
      <div className={`menu__panel menu__panel--${align}`}>{children}</div>
    </details>
  )
}
